/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import Tbl from '@appblocks/codegeneration/Libraries/Tbl';
import TableItem from '@appblocks/codegeneration/SettingsConfigurations/TableItem';

export const getFiles = (feature: BaseFeature): string[] => {
    return [
        'src/console_Main.js.gz',
        'src/console_Dashboard.js.gz',
        'src/dashboard_setup.json',
    ];
};

export const getCodeBlocks = (feature: BaseFeature): CodeBlock[] => {
    const blocks: Array<CodeBlock> = [];
    const project: ProjectConfiguration = feature.project;
    const GROUPS = feature.getSettingValue('GROUPS');
    const dashboardEnabled = feature.getEnabled();
    const appVersion = project.getSettingValue('general', 'FIRMWAREVERSION');
    const TABLES = feature.getSettingValue('TABLES');
    const appID = project.id;

    if (!feature.getEnabled()) {
        return blocks;
    }

    const projectVariables = project.getVariables();

    let variableGetCode = '';
    let variableSetCode = '';
    const variablesGetCode = [];
    let cmdExecCode = '';
    const widgets = feature.getSettingValue('WIDGETS');

    const widgetsArray = [];
    const variableInCode: { [variable: string]: boolean } = {};
    blocks.push({
        location: 'DASHBOARD_SETUP_TITLE',
        blockType: BlockType.LOCATION,
        template: `
    "title": "${feature.getSettingValue('DASHBOARD_TITLE')}"`,
    });
    let deviceVariablesFmt = '"{';
    const variables = [];
    if (widgets.length > 0 && dashboardEnabled) {
        for (let i = 0; i < widgets.length; i++) {
            const widget = widgets[i].getWidgetObject();
            let { variable, linkType } = widget;
            try {
                const item = project.getFeature(variable);
                if (item?.id === 'variable') {
                    variable = item.getSettingValue('VARNAME');
                    let varValue = project.convertDataType(variable, item.getSettingValue('DATATYPE'), TibboDataType.STRING, '');
                    if (item.getSettingValue('DATATYPE') === TibboDataType.STRING) {
                        varValue = `"\\x22"+${varValue}+"\\x22"`;
                    }
                    linkType = 'variable';
                    if (!Object.prototype.hasOwnProperty
                        .call(variableInCode, variable)) {
                        variables.push(item);
                        variableInCode[variable] = true;
                    }
                } else if (item?.id === 'command') {
                    variable = item.getSettingValue('NAME');
                    linkType = 'command';
                    if (!Object.prototype.hasOwnProperty
                        .call(variableInCode, variable)) {
                        cmdExecCode += `
if (strcmp(variable, "${variable}") == 0) {
    cmd_${variable}(value);
}
`;
                    }
                }
                widget.variable = variable;
                widget.linkType = linkType;
                widgetsArray.push(widget);
            } catch (ex) {
                widgets.splice(i, 1);
                i--;
            }
        }
    }

    let settingsCode = '';
    for (let i = 0; i < GROUPS.length; i++) {
        const tmpBlocks = GROUPS[i].getCodeBlocks();
        for (let j = 0; j < tmpBlocks.length; j++) {
            if (tmpBlocks[j].location === 'LUIS_GROUP_ITEM') {
                settingsCode += tmpBlocks[j].template;
                if (i + 1 < GROUPS.length) {
                    settingsCode += ',';
                }
            }
        }
    }
    blocks.push({
        location: 'DASHBOARD_SETTINGS_SCHEMA',
        template: settingsCode,
    });

    blocks.push({
        location: 'DASHBOARD_SETUP_WIDGETS',
        blockType: BlockType.CODE,
        template: `
        "updateInterval": ${feature.getSettingValue('INTERVAL') * 1000},
        "widgets": ${JSON.stringify(widgetsArray)},
        "widgetColumns": ${feature.getSettingValue('WEB_ADMIN_WIDGET_COLUMNS')},`,
    });

    blocks.push({
        location: 'DASHBOARD_APP_VERSION',
        blockType: BlockType.CODE,
        template: `
        "appName": "${project.name}",
        "appVersion": "${appVersion}",
        "appId": "${appID}",
        `,
    });

    const settingGroups = feature.getSettingValue('GROUPS');

    for (let i = 0; i < settingGroups.length; i += 1) {
        const group = settingGroups[i];
        const settings = group.getSettingValue('SETTINGS');
        for (let j = 0; j < settings.length; j += 1) {
            const setting = settings[j];
            const settingItem = project.getFeature(setting.getSettingValue('SETTINGID'));
            if (!settingItem) {
                continue;
            }
            const variable = projectVariables.find((v: any) => v.getSettingValue('FEATURE') === settingItem.getSettingValue('ID'));
            if (!variable) {
                continue;
            }
            if (!Object.prototype.hasOwnProperty
                .call(variableInCode, variable.getSettingValue('VARNAME'))) {
                variableInCode[variable.getSettingValue('VARNAME')] = true;
                variables.push(variable);
            }
        }
    }

    for (let i = 0; i < variables.length; i += 1) {
        const variable = variables[i];
        const VARNAME = variable.getSettingValue('VARNAME');
        let varValue = project.convertDataType(VARNAME, variable.getSettingValue('DATATYPE'), TibboDataType.STRING);
        if (variable.getSettingValue('DATATYPE') === TibboDataType.STRING) {
            varValue = `"\\x22"+${varValue}+"\\x22"`;
        }

        let formatDataType = '%d';
        let formatDataTypeRaw = formatDataType;
        switch (variable.getSettingValue('DATATYPE')) {
            case TibboDataType.STRING:
                formatDataType = '%s';
                formatDataTypeRaw = formatDataType;
                break;
            case TibboDataType.FLOAT:
                formatDataType = '%f';
                formatDataTypeRaw = formatDataType;
                break;
            case TibboDataType.BOOLEAN:
                formatDataType = '%d';
                formatDataTypeRaw = formatDataType;
                break;
            case TibboDataType.INTEGER:
                formatDataType = '%d';
                formatDataTypeRaw = formatDataType;
                break;
            default:
                formatDataType = '%d';
                formatDataTypeRaw = formatDataType;
                break;
        }
        variableGetCode += `
        if (strcmp(variable,"${VARNAME}")== 0) {
            var_value = ${project.convertDataType(VARNAME, variable.getSettingValue('DATATYPE'), TibboDataType.STRING, '')};
        }`;
        variableSetCode += `
        if (strcmp(variable, "${VARNAME}") == 0) {
            var_${VARNAME}_set(${project.convertDataType('value', TibboDataType.STRING, variable.getSettingValue('DATATYPE'))});
            snprintk(json_msg, sizeof(json_msg), "${formatDataType}", ${VARNAME});
        }`;

        variablesGetCode.push(`\\"${VARNAME}\\":${formatDataType}`);
    }

    deviceVariablesFmt += variablesGetCode.join(',');
    deviceVariablesFmt += '}"';

    blocks.push({
        location: 'CMAKE',
        blockType: BlockType.CODE,
        template: `
set(source_file_index src/console_Main.js.gz)
generate_inc_file_for_target(app \${source_file_index} \${gen_dir}/console_Main.js.gz.inc)

set(source_file_index src/console_Dashboard.js.gz)
generate_inc_file_for_target(app \${source_file_index} \${gen_dir}/console_Dashboard.js.gz.inc)

set(source_file_index src/dashboard_setup.json)
generate_inc_file_for_target(app \${source_file_index} \${gen_dir}/dashboard_setup.json.gz.inc  --gzip)

`,
    });

    blocks.push({
        location: 'GLOBAL_DEFINES',
        blockType: BlockType.CODE,
        template: `

static const uint8_t console_main_gz[] = {
    #include "console_Main.js.gz.inc"
};

struct http_resource_detail_static console_main_gz_resource_detail = {
    .common = {
        .bitmask_of_supported_http_methods = BIT(HTTP_GET),
        .type = HTTP_RESOURCE_TYPE_STATIC,
        .content_encoding = "gzip",
        .content_type = "application/javascript",
    },
    .static_data = console_main_gz,
    .static_data_len = sizeof(console_main_gz),
};

HTTP_RESOURCE_DEFINE(console_main_gz_resource, my_service, "/console_Main.js",
                     &console_main_gz_resource_detail);

static const uint8_t console_dashboard_gz[] = {
    #include "console_Dashboard.js.gz.inc"
};

struct http_resource_detail_static console_dashboard_gz_resource_detail = {
    .common = {
        .bitmask_of_supported_http_methods = BIT(HTTP_GET),
        .type = HTTP_RESOURCE_TYPE_STATIC,
        .content_encoding = "gzip",
        .content_type = "application/javascript",
    },
    .static_data = console_dashboard_gz,
    .static_data_len = sizeof(console_dashboard_gz),
};

HTTP_RESOURCE_DEFINE(console_dashboard_gz_resource, my_service, "/console_Dashboard.js",
                     &console_dashboard_gz_resource_detail);

static const uint8_t dashboard_setup_gz[] = {
    #include "dashboard_setup.json.gz.inc"
};

struct http_resource_detail_static dashboard_setup_gz_resource_detail = {
    .common = {
        .bitmask_of_supported_http_methods = BIT(HTTP_GET),
        .type = HTTP_RESOURCE_TYPE_STATIC,
        .content_encoding = "gzip",
        .content_type = "application/json",
    },
    .static_data = dashboard_setup_gz,
    .static_data_len = sizeof(dashboard_setup_gz),
};

HTTP_RESOURCE_DEFINE(dashboard_setup_gz_resource, my_service, "/dashboard_setup.json",
                     &dashboard_setup_gz_resource_detail);
`,
    });

    blocks.push({
        location: 'GLOBAL_INCLUDES',
        id: '#include <zephyr/sys/reboot.h>',
        blockType: BlockType.CODE,
        template: '#include <zephyr/sys/reboot.h>',
    });

    blocks.push({
        location: 'FUNCTION_DEFINITIONS',
        blockType: BlockType.CODE,
        template: `
#define DEVICE_STATE_FMT "{\\"firmwareVersion\\":\\"%s\\",\\"board\\":\\"%s\\"}"

#define DEVICE_VARIABLES_FMT ${deviceVariablesFmt}

char* get_url_param(const char *url, const char *field) {
    int pc=0;
    char *tok;
    char *otok;
    char *key;
    char *value;
    char urlParams[255] = "";
    memcpy(urlParams, &url[0], strlen(url));
    // if first character is ?, remove it
    if (urlParams[0] == '?') {
        memmove(urlParams, urlParams + 1, strlen(url)-1);
    }
    for(tok=strtok((char *)urlParams,"&");tok!=NULL;tok=strtok(tok,"&")) {
        pc++;
        otok=tok+strlen(tok)+1;
        tok=strtok(tok,"=");
        key=tok;
        tok=strtok(NULL,"=");
        value=tok;
        tok=otok;
        if (strcmp(key,field)==0) {
            return value;
        }
    };
    return "";
}

static uint8_t recv_buffer[1024];

static int dyn_handler(struct http_client_ctx *client,
                       enum http_data_status status, uint8_t *buffer,
                       size_t len, void *user_data)
{
    static bool response_sent;

    static uint8_t data_payload_buf[255];

    // LOG_INF("API handler status %d, response_sent %d", status, response_sent);

    switch (status) {
        case HTTP_SERVER_DATA_ABORTED: {
            response_sent = false;
            return 0;
        }

        case HTTP_SERVER_DATA_MORE: {
            /* A payload is not expected with the GET request. Ignore any data and wait until
            * final callback before sending response
            */
            return 0;
        }

        case HTTP_SERVER_DATA_FINAL: {
            if (HTTP_DEBUG_PRINT) {
                LOG_INF("%s", client->url_buffer);
            }
            if (response_sent) {
                /* Response already sent, return 0 to indicate to server that the callback
                * does not need to be called again.
                */
                response_sent = false;
                return 0;
            }
            memcpy(data_payload_buf, buffer, len);

            char json_msg[255] = "";

            char urlParams[255] = "";
            memcpy(urlParams, &data_payload_buf[0], len);
            // if first character is ?, remove it
            if (urlParams[0] == '?') {
                memmove(urlParams, urlParams + 1, len-1);
            }
            char *endpoint = get_url_param((const char *)urlParams, "e");
            
            if (HTTP_DEBUG_PRINT) {
                // LOG_INF("endpoint: %s", endpoint);
            }
            char sbuf[255];
            if (strcmp(endpoint,"i")==0) {
                char *action = get_url_param((const char *)urlParams, "action");
                // information endpoint
                char buf[NET_IPV4_ADDR_LEN];
                snprintk(json_msg, sizeof(json_msg), DEVICE_STATE_FMT,
                        CONFIG_BOARD_TARGET,
                        CONFIG_BOARD_TARGET
                        );
                
            } else if (strcmp(endpoint,"v")==0) {
                char *action = get_url_param((const char *)urlParams, "action");
                // dim variable as string=http_server_find_param(params, "variable")
                // action=http_server_find_param(params, "action")
                // value=http_server_decode(http_server_find_param(params, "value"))
                // if action="get" then
                char *variable = get_url_param((const char *)urlParams, "variable");
                string value = get_url_param((const char *)urlParams, "value");

                // variables endpoint
                if (strcmp(action,"get")==0) {
                /****HTTP_VARIABLE_VALUE_GET****/
                    string var_value = "";
                    ${variableGetCode}
                    snprintk(json_msg, sizeof(json_msg), "%s", var_value.c_str());
                } else if (strcmp(action,"set")==0) {
                /****HTTP_VARIABLE_VALUE_SET****/
                    ${variableSetCode}
                }
                
            } else if (strcmp(endpoint, "w") == 0) {
                // all variables
                
                
                snprintk(json_msg, sizeof(json_msg), DEVICE_VARIABLES_FMT ${Object.keys(variableInCode).length > 0 ? ',' : ''} ${Object.keys(variableInCode).join(',')});

            } else if (strcmp(endpoint, "f") == 0) {
                // command
                char *variable = get_url_param((const char *)urlParams, "variable");
                char *value = get_url_param((const char *)urlParams, "value");

                ${cmdExecCode}
                snprintk(json_msg, sizeof(json_msg), "{}");
            } else if (strcmp(endpoint,"s")==0) { 
                char *cmd = get_url_param((const char *)urlParams, "cmd");
                if (strcmp(cmd,"E")==0) {
                    // reboot
                    sys_reboot(SYS_REBOOT_WARM);
                }
            } else {
                // unknown endpoint
                snprintk(json_msg, sizeof(json_msg), "{}");
                
            }

            enum http_method method = client->method;
            if (strcmp(http_method_str(method), "GET") == 0) {
                response_sent = true;
            }

            size_t resp_len;

            for (size_t i = 0; i < strlen(json_msg) + 1; ++i) {
                buffer[i] = json_msg[i];
                resp_len = i;
            }

            return resp_len;
        }
        default: {
            LOG_WRN("Unexpected status %d", status);
            return -1;
        }
    }
}

struct http_resource_detail_dynamic dyn_resource_detail = {
    .common = {
        .bitmask_of_supported_http_methods =
                BIT(HTTP_GET) | BIT(HTTP_POST),
        .type = HTTP_RESOURCE_TYPE_DYNAMIC,
    },
    .cb = dyn_handler,
    .data_buffer = recv_buffer,
    .data_buffer_len = sizeof(recv_buffer),
    .user_data = NULL,
};


HTTP_RESOURCE_DEFINE(dyn_resource, my_service, "/api.html",
                     &dyn_resource_detail);


`,
    });


    return blocks;
};
